import React, { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { StatusBadge, PriorityBadge, SlaBadge } from "../components/Badges";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtDuration } from "../lib/format";
import { Search, ChevronLeft, ChevronRight } from "lucide-react";

const ANY = "ALL";

export default function AdminTickets() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ANY);
  const [priority, setPriority] = useState(ANY);
  const [slaStatus, setSlaStatus] = useState(ANY);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const params = { page, page_size: pageSize };
  if (search) params.search = search;
  if (status !== ANY) params.status = status;
  if (priority !== ANY) params.priority = priority;
  if (slaStatus !== ANY) params.sla_status = slaStatus;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-tickets", params],
    queryFn: () => api.get("/admin/tickets", { params }).then((r) => r.data),
    placeholderData: keepPreviousData,
  });
  const statusesQ = useQuery({ queryKey: ["ticket-statuses"], queryFn: () => api.get("/ticket-statuses").then((r) => r.data) });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1;

  const reset = (setter) => (v) => { setter(v); setPage(1); };

  return (
    <Layout>
      <div className="mb-6">
        <h1 className="font-heading font-black tracking-tighter text-4xl">All Tickets</h1>
        <p className="text-muted-foreground mt-1">Manage and track every support request</p>
      </div>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Search number, subject, customer…" className="pl-9" data-testid="admin-search" />
        </div>
        <Select value={status} onValueChange={reset(setStatus)}>
          <SelectTrigger className="w-[160px]" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All Statuses</SelectItem>
              {statusesQ.data?.map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priority} onValueChange={reset(setPriority)}>
          <SelectTrigger className="w-[140px]" data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All Priority</SelectItem>
            <SelectItem value="LOW">Low</SelectItem>
            <SelectItem value="MEDIUM">Medium</SelectItem>
            <SelectItem value="HIGH">High</SelectItem>
          </SelectContent>
        </Select>
        <Select value={slaStatus} onValueChange={reset(setSlaStatus)}>
          <SelectTrigger className="w-[150px]" data-testid="filter-sla"><SelectValue placeholder="SLA" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>All SLA</SelectItem>
            {["NOT_STARTED","IN_PROGRESS","AT_RISK","MET","BREACHED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-muted-foreground">
            <tr className="text-left">
              <th className="px-4 py-3 font-medium">Ticket</th>
              <th className="px-4 py-3 font-medium">Customer</th>
              <th className="px-4 py-3 font-medium">Subject</th>
              <th className="px-4 py-3 font-medium">Priority</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Progress</th>
              <th className="px-4 py-3 font-medium">Resolution</th>
              <th className="px-4 py-3 font-medium">SLA</th>
              <th className="px-4 py-3 font-medium">Updated</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {isLoading ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
            ) : data?.items.length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No tickets match your filters.</td></tr>
            ) : (
              data?.items.map((t) => (
                <tr key={t.id} className="hover:bg-secondary/40 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Link to={`/admin/tickets/${t.id}`} data-testid={`admin-ticket-${t.ticket_number}`} className="font-mono-plex text-xs text-primary hover:underline">{t.ticket_number}</Link>
                      {t.has_unread && (
                        <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {t.unread_count}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap max-w-[160px] truncate">{t.customer_name}</td>
                  <td className="px-4 py-3 max-w-[220px] truncate font-medium">
                    <div className="flex items-center gap-2">
                      <span>{t.subject}</span>
                      {t.has_unread && <span className="text-[10px] font-semibold uppercase tracking-wide text-red-500">Unread</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={t.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} statusMeta={t.status_meta} /></td>
                  <td className="px-4 py-3 w-28"><Progress value={t.progress} className="h-1.5" /><span className="text-[10px] text-muted-foreground">{t.progress}%</span></td>
                  <td className="px-4 py-3 text-xs whitespace-nowrap font-mono-plex">{fmtDuration(t.sla.resolution_seconds)}</td>
                  <td className="px-4 py-3"><SlaBadge status={t.sla.status} /></td>
                  <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(t.updated_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4">
        <span className="text-sm text-muted-foreground">{data?.total ?? 0} tickets · Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="prev-page"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="next-page"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
    </Layout>
  );
}
